import { motion } from 'framer-motion';
import { Link } from 'react-router-dom'; // Make sure this import exists
import { 
  CalendarIcon, 
  UserGroupIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  CommandLineIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { FaCar } from 'react-icons/fa';
import { IoCarOutline } from 'react-icons/io5';

const AgentCard = ({ 
  icon: Icon, 
  title, 
  description, 
  features,
  delay 
}: { 
  icon: any; 
  title: string; 
  description: string;
  features: string[];
  delay: number;
}) => (
  <Link to="/services/ai-appointment-setters">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="relative group min-h-[300px] cursor-pointer"
    >
      <div className="relative bg-white/50 backdrop-blur-sm rounded-3xl border border-primary/10 hover:border-[#FF7066]/20 p-8 hover:shadow-xl hover:shadow-[#FF7066]/5 transition-all duration-300">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-[#FF7066]/10 transition-colors duration-300">
            <Icon className="w-6 h-6 text-primary group-hover:text-[#FF7066] transition-colors duration-300" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-[#FF7066] transition-colors duration-300">{title}</h3>
            <p className="text-secondary/80">{description}</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-secondary/80">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/60 group-hover:bg-[#FF7066] transition-colors duration-300" />
              {feature}
            </div>
          ))}
        </div>
        
        {/* Learn more indicator */}
        <div className="mt-4 text-primary font-medium group-hover:text-[#FF7066] transition-colors duration-300 flex items-center gap-1">
          <span>Learn more</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </motion.div>
  </Link>
);

const AIAgents = () => {
  const agents = [
    {
      icon: CalendarIcon,
      title: "AI Appointment Booker",
      description: "Our flagship agent revolutionising appointment scheduling across multiple industries.",
      features: [
        "Natural language understanding for seamless communication",
        "Smart scheduling optimisation",
        "Multi-calendar integration",
        "Automated follow-ups and reminders",
        "Industry-specific customisation"
      ],
      delay: 0.1
    },
    {
      icon: UserGroupIcon,
      title: "Team Coordination Agent",
      description: "Intelligent agent that manages team workflows and project coordination.",
      features: [
        "Task assignment and tracking",
        "Team availability management",
        "Project timeline optimisation",
        "Resource allocation",
        "Performance analytics"
      ],
      delay: 0.2
    },
    {
      icon: BuildingOfficeIcon,
      title: "Business Process Agent",
      description: "Hierarchical agent system for complex business operations.",
      features: [
        "Multi-level decision making",
        "Process automation",
        "Workflow optimisation",
        "Compliance monitoring",
        "Custom business rules"
      ],
      delay: 0.3
    }
  ];

  const industries = [
    { icon: ChatBubbleLeftRightIcon, name: "Catering Companies" },
    { icon: BuildingOfficeIcon, name: "Restaurants" },
    { icon: IoCarOutline, name: "Car Dealerships" },
    { icon: CommandLineIcon, name: "Service Providers" },
    { icon: ArrowPathIcon, name: "Healthcare" },
    { icon: SparklesIcon, name: "More Coming Soon" }
  ];

  return (
    <section id="ai-agents" className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-[#FF7066]/5 to-white">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 112, 102, 0.03), transparent 70%)',
        }}
      />
      
      {/* Floating Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className="absolute top-20 left-20 w-64 h-64 bg-[#FF7066]/10 rounded-full blur-3xl"
      />
      
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
            >
              AI Agents
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-primary mb-6"
            >
              Intelligent <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">AI Agents</span> for Every Need
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-xl text-secondary mb-8"
            >
              From appointment scheduling to complex business operations, our AI agents are transforming how businesses work.
            </motion.p>
          </div>

          {/* Agents Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {agents.map((agent) => (
              <AgentCard
                key={agent.title}
                icon={agent.icon}
                title={agent.title}
                description={agent.description}
                features={agent.features}
                delay={agent.delay}
              />
            ))}
          </div>

          {/* Industries Section */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-2xl font-bold text-primary mb-6"
            >
              Currently Testing with Industry Leaders
            </motion.h3>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
          >
            {industries.map((industry, index) => (
              <div
                key={industry.name}
                className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/50 backdrop-blur-sm border border-primary/10 hover:border-[#FF7066]/20 transition-all duration-300 group"
              >
                <industry.icon className="w-6 h-6 text-primary group-hover:text-[#FF7066] transition-colors duration-300" />
                <span className="text-sm text-secondary text-center">{industry.name}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AIAgents;
